import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMountedRef } from "@/hooks/useMountedRef";

type AsyncSubmitAction = (signal: AbortSignal) => Promise<void>;

export function useAsyncSubmit() {
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useMountedRef();

  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);

  const submit = useCallback(async (action: AsyncSubmitAction) => {
    if (submittingRef.current) return;

    const abortController = new AbortController();
    submittingRef.current = true;
    abortControllerRef.current = abortController;
    setSubmitting(true);

    try {
      await action(abortController.signal);
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }

      submittingRef.current = false;
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [mountedRef]);

  return { submitting, submit };
}
